import React from "react";

/**
 * OMCi v3 Button (Figma component set 25:40).
 * Small = 40px (px-14), Medium = 44px (px-16); r8, gap 8, label Inter Medium 14/20.
 */

const SIZE_CLASS = {
    small: "h-10 px-3.5",
    medium: "h-11 px-4",
};

const VARIANT_CLASS = {
    primary: "bg-primary text-white enabled:hover:bg-primary-hover enabled:active:bg-primary-active disabled:bg-subtle disabled:text-ink-disabled",
    secondary: "bg-surface text-ink border border-line enabled:hover:bg-tool-global-hover enabled:active:bg-selected disabled:border-line-subtle disabled:text-ink-disabled",
    destructive: "bg-destructive text-white enabled:hover:bg-destructive-hover enabled:active:bg-destructive-active disabled:bg-subtle disabled:text-ink-disabled",
};

export const Button = React.forwardRef(({ variant = "secondary", size = "small", className = "", children, type = "button", ...props }, ref) => (
    <button
        ref={ref}
        type={type}
        className={`inline-flex items-center justify-center gap-2 rounded-omc text-sm leading-5 font-medium whitespace-nowrap enabled:cursor-pointer disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-line-focus ${SIZE_CLASS[size]} ${VARIANT_CLASS[variant]} ${className}`}
        {...props}
    >
        {children}
    </button>
));

Button.displayName = "Button";

// Anchors never match `:enabled`, so the link variants spell their hover/active states plainly.
const LINK_VARIANT_CLASS = {
    primary: "bg-primary text-white hover:bg-primary-hover active:bg-primary-active",
    secondary: "bg-surface text-ink border border-line hover:bg-tool-global-hover active:bg-selected",
};

/**
 * A link with the Button's geometry and variants, for a navigation the design
 * shows as a button (e.g. "Sign in with OMCi", a hosted-UI redirect).
 */
export const LinkButton = React.forwardRef(({ variant = "secondary", size = "small", className = "", children, ...props }, ref) => (
    <a
        ref={ref}
        className={`inline-flex items-center justify-center gap-2 rounded-omc text-sm leading-5 font-medium whitespace-nowrap cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-line-focus ${SIZE_CLASS[size]} ${LINK_VARIANT_CLASS[variant]} ${className}`}
        {...props}
    >
        {children}
    </a>
));

LinkButton.displayName = "LinkButton";

/**
 * 40×40 icon-only action (secondary treatment); pass the 18px icon as children.
 */
export const IconButton = ({ className = "", children, type = "button", ...props }) => (
    <button
        type={type}
        className={`flex flex-shrink-0 items-center justify-center w-10 h-10 rounded-omc border border-line bg-surface text-icon enabled:cursor-pointer enabled:hover:bg-tool-global-hover disabled:cursor-not-allowed disabled:border-line-subtle disabled:text-ink-disabled focus:outline-none focus-visible:ring-2 focus-visible:ring-line-focus ${className}`}
        {...props}
    >
        {children}
    </button>
);
